/**
 * El espacio de trabajo: empresa, delegaciones y sociedades hermanas.
 *
 * Lo pide la cabecera y no se cachea entre sesiones: si alguien cambia de
 * empresa, lo último que conviene es que la anterior siga pintada durante
 * unos segundos.
 */

import { useEffect, useState } from 'react';
import type { ApiClient } from './api-client.js';

export interface WorkspaceCompany {
  id: string;
  taxId: string;
  legalName: string;
  tradeName: string | null;
}

export interface WorkspaceBranch {
  id: string;
  code: string;
  name: string;
}

export interface Workspace {
  company: WorkspaceCompany;
  user: {
    id: string;
    email: string;
    fullName: string;
    roles: string[];
    permissions: string[];
  };
  branches: WorkspaceBranch[];
  /** `false` = sin restricción de delegación, que NO es «ninguna». */
  scopedToBranches: boolean;
  otherCompanies: WorkspaceCompany[];
}

export function useWorkspace(api: ApiClient): Workspace | null {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  useEffect(() => {
    let cancelled = false;

    api
      .get<Workspace>('/web/session/workspace')
      .then((data) => {
        if (!cancelled) setWorkspace(data);
      })
      .catch(() => {
        // Un fallo aquí no debe tumbar el ERP: la cabecera se pinta sin
        // nombre de empresa, que es peor que con él pero mejor que una
        // pantalla en blanco. El 401, si lo hubo, ya lo gestionó el
        // cliente HTTP cerrando la sesión.
        if (!cancelled) setWorkspace(null);
      });

    return () => {
      cancelled = true;
    };
  }, [api]);

  return workspace;
}

/**
 * Cómo se describe el ámbito de delegación en la cabecera.
 *
 * La distinción importa: **sin restricción** no es lo mismo que **ninguna
 * delegación**. Decirle «ninguna» a un director le haría creer que trabaja
 * limitado cuando ve toda la empresa.
 */
export function describeScope(workspace: Workspace): string {
  if (!workspace.scopedToBranches) return 'Todas las delegaciones';
  if (workspace.branches.length === 0) return 'Sin delegación asignada';
  if (workspace.branches.length === 1) return workspace.branches[0]!.name;
  return `${workspace.branches.length} delegaciones`;
}
